import React, { useState, useEffect } from 'react';
import {
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { toastSuccess, toastError } from '../utils/toast';
import { useApi } from '../contexts/ApiContext';
import { AiApiKeyTypes, AiLlmTypes } from '@flashbacktech/flashbackclient/api';
import { useWithTokenRefresh } from '../utils/api';

function AIApiKeys() {
  const { authState, globalData } = useAuth();
  const [apiKeys, setApiKeys] = useState<AiApiKeyTypes.RepoAiApiKeyDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  // Repository selection
  const [selectedRepoId, setSelectedRepoId] = useState<string>('');
  const [repos, setRepos] = useState<any[]>([]);


  // Create Modal
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [createData, setCreateData] = useState<AiApiKeyTypes.CreateRepoAiApiKeyRequest>({
    name: '',
    aiType: AiLlmTypes.AiType.OPENAI,
  });

  // Secret Display Modal (ONE TIME ONLY!)
  const [openSecretModal, setOpenSecretModal] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ id: string; key: string; secret: string } | null>(
    null
  );
  const [secretCopied, setSecretCopied] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  // Edit Modal
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<AiApiKeyTypes.RepoAiApiKeyDTO | null>(null);
  const [editName, setEditName] = useState('');

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<AiApiKeyTypes.RepoAiApiKeyDTO | null>(null);

  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedKeyForMenu, setSelectedKeyForMenu] = useState<AiApiKeyTypes.RepoAiApiKeyDTO | null>(null);

  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();

  // Get available AI LLMs from selected repository
  const getAvailableAiLlms = (): { id: string; name: string; aiType: string }[] => {
    const selectedRepo = repos.find(r => r.id === selectedRepoId);
    if (!selectedRepo || !selectedRepo.aiLlms || selectedRepo.aiLlms.length === 0) {
      console.log('[AIApiKeys] No aiLlms found for repo:', selectedRepo?.name);
      return [];
    }
    
    console.log('[AIApiKeys] aiLlms for repo:', selectedRepo.name, selectedRepo.aiLlms);
    
    // Extract AI LLMs with their details
    return selectedRepo.aiLlms
      .map((llm: any) => {
        const aiLlmId = llm.aiLlmId || llm.id;
        const aiType = llm.aiLlm?.aiType || llm.aiType || 'OTHER';
        const name = llm.aiLlm?.name || llm.name || aiType;
        console.log('[AIApiKeys] LLM:', { aiLlmId, aiType, name, llm });
        return { id: aiLlmId, name, aiType: aiType.toUpperCase() };
      })
      .filter((llm: any) => llm.id);
  };

  const availableAiLlms = getAvailableAiLlms();

  // AI type labels for display
  const aiTypeLabels: Record<string, string> = {
    [AiLlmTypes.AiType.OPENAI]: 'OpenAI',
    [AiLlmTypes.AiType.GOOGLE]: 'Google',
    [AiLlmTypes.AiType.ANTHROPIC]: 'Anthropic',
    [AiLlmTypes.AiType.AWS]: 'AWS Bedrock',
    [AiLlmTypes.AiType.OTHER]: 'Other',
  };

  // Load repos when workspace changes
  useEffect(() => {
    loadRepos();
  }, [selectedWorkspaceId, globalData?.workspaces]);

  // Listen for reload event from parent
  useEffect(() => {
    const handleReload = () => { loadRepos(); if (selectedRepoId) loadApiKeys(); };
    window.addEventListener('reloadApiKeysData', handleReload);
    return () => window.removeEventListener('reloadApiKeysData', handleReload);
  }, [selectedWorkspaceId, globalData?.workspaces, selectedRepoId]);

  // Load API keys automatically when selectedRepoId changes
  useEffect(() => {
    if (selectedRepoId) {
      console.log('[AIApiKeys] Loading API keys for repo:', selectedRepoId);
      loadApiKeys();
    }
  }, [selectedRepoId]);

  const loadRepos = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://backend.flashback.tech';
      
      console.log('[AIApiKeys] Loading repos for workspace:', selectedWorkspaceId);
      
      // Si ALL, charger les repos de tous les workspaces accessibles
      if (selectedWorkspaceId === 'ALL') {
        if (!globalData?.workspaces || globalData.workspaces.length === 0) {
          console.log('[AIApiKeys] No workspaces available');
          setRepos([]);
          setSelectedRepoId('');
          return;
        }
        
        // Charger les repos de tous les workspaces
        const allRepos: any[] = [];
        for (const workspace of globalData.workspaces) {
          try {
            const response = (await withTokenRefresh(() => apiClient.getStorageRepos(workspace.id))) as any;
            if (response.success && response.repos) {
              allRepos.push(...response.repos);
            }
          } catch (err) {
            console.error(`[AIApiKeys] Error loading repos for workspace ${workspace.id}:`, err);
          }
        }
        
        console.log('[AIApiKeys] Loaded repos (ALL workspaces):', allRepos.length);
        setRepos(allRepos);
        if (allRepos.length > 0 && !selectedRepoId) {
          setSelectedRepoId(allRepos[0].id);
        }
      } else if (selectedWorkspaceId) {
        // Charger les repos du workspace sélectionné
        const response = (await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId))) as any;
        if (response.success && response.repos) {
          setRepos(response.repos);
          // Auto-select first repo si aucun n'est sélectionné
          if (response.repos && response.repos.length > 0 && !selectedRepoId) {
            setSelectedRepoId(response.repos[0].id);
          }
        }
      } else {
        console.log('[AIApiKeys] No workspace selected');
        setRepos([]);
        setSelectedRepoId('');
      }
    } catch (error) {
      console.error('[AIApiKeys] Error loading repos:', error);
      toastError('Failed to load repositories');
    }
  };

  const loadApiKeys = async () => {
    if (!selectedRepoId) return;

    setLoading(true);
    try {
      const response = await withTokenRefresh(() => apiClient.getRepoAiApiKeys(selectedRepoId)) as any;
      if (response.success) {
        setApiKeys(response.apiKeys || []);
      }
    } catch (error: any) {
      console.error('[AIApiKeys] Error loading API keys:', error);
      toastError(error.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!selectedRepoId) {
      toastError('Please select a repository');
      return;
    }
    if (!createData.name) {
      toastError('Name is required');
      return;
    }

    try {
      const selectedRepo = repos.find(r => r.id === selectedRepoId);
      console.log('[AIApiKeys] Creating API key with:', { 
        repoId: selectedRepoId, 
        repoName: selectedRepo?.name,
        createData,
        availableAiLlms: selectedRepo?.aiLlms 
      });
      const response = await withTokenRefresh(() => apiClient.createRepoAiApiKey(selectedRepoId, createData)) as any;
      console.log('[AIApiKeys] Create response:', response);
      if (response.success) {
        setCreatedKey({
          id: response.id,
          key: response.key,
          secret: response.secret,
        });
        setOpenCreateModal(false);
        setOpenSecretModal(true); // Show ONE-TIME secret modal
        setCreateData({ name: '', aiType: AiLlmTypes.AiType.OPENAI });
        loadApiKeys();
        toastSuccess('API key created successfully');
        // Notify rest of app for onboarding mission
        try { localStorage.setItem('hasRepoWithApiKey', 'true'); } catch { /* Ignore */ }
        try { window.dispatchEvent(new CustomEvent('repoApiKeyCreated')); } catch { /* Ignore */ }
      } else {
        toastError(response.message || 'Failed to create AI API key');
      }
    } catch (error: any) {
      console.error('[AIApiKeys] Error creating API key:', error);
      toastError(error.message || 'Failed to create API key');
    }
  };

  const handleEditApiKey = async () => {
    if (!editingKey || !selectedRepoId) return;

    try {
      await withTokenRefresh(() => apiClient.updateRepoAiApiKey(selectedRepoId, editingKey.id, { name: editName }));
      toastSuccess('API key updated successfully');
      setOpenEditModal(false);
      setEditingKey(null);
      loadApiKeys();
    } catch (error: any) {
      console.error('[AIApiKeys] Error updating API key:', error);
      toastError(error.message || 'Failed to update API key');
    }
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete || !selectedRepoId) return;

    try {
      const response = await withTokenRefresh(() => apiClient.deleteRepoAiApiKey(selectedRepoId, keyToDelete.id)) as any;
      toastSuccess(response.message || 'AI API key deleted successfully');
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
      loadApiKeys();
    } catch (error: any) {
      console.error('[AIApiKeys] Error deleting API key:', error);
      toastError(error.message || 'Failed to delete API key');
    }
  };

  const copyToClipboard = (text: string, type: 'key' | 'secret') => {
    navigator.clipboard.writeText(text);
    if (type === 'key') {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } else {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
    toastSuccess('Copied to clipboard');
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, apiKey: AiApiKeyTypes.RepoAiApiKeyDTO) => {
    setAnchorEl(event.currentTarget);
    setSelectedKeyForMenu(apiKey);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedKeyForMenu(null);
  };

  const getAITypeColor = (aiType: AiLlmTypes.AiType) => {
    switch (aiType) {
      case AiLlmTypes.AiType.OPENAI:
        return { bg: '#EAF8F1', color: '#1F8F6B', border: '#D1FAE5' };
      case AiLlmTypes.AiType.GOOGLE:
        return { bg: '#F0F2FF', color: '#617BFF', border: '#D6DDFF' };
      case AiLlmTypes.AiType.ANTHROPIC:
        return { bg: '#FFF7E6', color: '#C38A00', border: '#FEF3C7' };
      case AiLlmTypes.AiType.AWS:
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FEE2E2' };
      case AiLlmTypes.AiType.OTHER:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Filter API keys by search
  const filteredApiKeys = apiKeys.filter((apiKey) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return apiKey.name.toLowerCase().includes(query) || (apiKey.key || '').toLowerCase().includes(query);
  });

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <Typography
            variant="h5"
            style={{
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '30px',
              color: '#000000',
            }}
          >
            AI API Keys
          </Typography>
          <Typography
            variant="body1"
            style={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#535862',
              marginTop: '4px',
              fontWeight: 400,
            }}
          >
            Manage repository-specific API keys for AI-powered features
          </Typography>
        </div>

        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
            }}
            title="Search"
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </button>
          <button
            onClick={() => {
              if (!selectedRepoId) {
                toastError('Please select a repository first');
                return;
              }
              setCreateData({ name: '', aiType: AiLlmTypes.AiType.OPENAI });
              setOpenCreateModal(true);
            }}
            data-tutorial="generate-key"
            className="px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors"
            style={{
              backgroundColor: '#617BFF',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#5A6FE8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#617BFF';
            }}
          >
            Create API Key
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search API keys..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              fontSize: '14px',
            }}
          />
        </div>
      )}

      <div className="border-b border-gray-200 mb-6"></div>

      {/* Repository Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
          Select Repository <span style={{ color: '#DC2626' }}>*</span>
        </label>
        <select
          value={selectedRepoId}
          onChange={(e) => setSelectedRepoId(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ fontSize: '14px' }}
          data-tutorial="select-repo"
        >
          <option value="">Select a repository</option>
          {repos.map((repo) => (
            <option key={repo.id} value={repo.id}>
              {repo.name}
            </option>
          ))}
        </select>
        {selectedWorkspaceId === 'ALL' && (
          <p className="mt-1 text-xs" style={{ color: '#F59E0B' }}>
            Please select a workspace first to see repositories
          </p>
        )}
        {!selectedRepoId && repos.length > 0 && (
          <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
            Select a repository to manage its AI API keys
          </p>
        )}
      </div>

      {/* API Keys List */}
      {!selectedRepoId ? (
        <div className="text-center py-12">
          <Typography style={{ color: '#667085', fontSize: '14px' }}>
            Select a repository to view its AI API keys
          </Typography>
        </div>
      ) : loading ? (
        <div className="text-center py-12">
          <Typography style={{ color: '#667085' }}>Loading API keys...</Typography>
        </div>
      ) : filteredApiKeys.length === 0 ? (
        <div className="text-center py-12">
          <Typography style={{ color: '#667085', fontSize: '14px' }}>
            {searchQuery
              ? 'No API keys found matching your search'
              : 'No API keys yet. Create your first API key to get started.'}
          </Typography>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredApiKeys.map((apiKey) => {
            const colors = getAITypeColor(apiKey.aiType);

            return (
              <div
                key={apiKey.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {/* AI Type Chip */}
                      <Chip
                        label={apiKey.aiType}
                        size="small"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.color,
                          border: `1px solid ${colors.border}`,
                          fontWeight: 600,
                          fontSize: '12px',
                          height: '22px',
                        }}
                      />

                      {/* Created Date */}
                      <Typography
                        style={{
                          fontSize: '12px',
                          color: '#667085',
                        }}
                      >
                        Created {formatDate(typeof apiKey.createdAt === 'string' ? apiKey.createdAt : apiKey.createdAt?.toISOString?.() || '')}
                      </Typography>
                    </div>

                    <Typography
                      style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: '#101828',
                        marginBottom: '8px',
                      }}
                    >
                      {apiKey.name}
                    </Typography>

                    {/* API Key (public) */}
                    <div className="flex items-center gap-2 mb-2">
                      <code
                        className="px-2 py-1 rounded text-sm font-mono"
                        style={{
                          backgroundColor: '#F9FAFB',
                          color: '#374151',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {apiKey.key}
                      </code>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(apiKey.key || '', 'key')}
                        title="Copy key"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </div>

                    <Typography
                      style={{
                        fontSize: '12px',
                        color: '#98A2B3',
                      }}
                    >
                      Repository: {apiKey.repoId}
                    </Typography>
                  </div>

                  <IconButton onClick={(e) => handleMenuClick(e, apiKey)} size="small">
                    <MoreVertIcon />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedKeyForMenu) {
              setEditingKey(selectedKeyForMenu);
              setEditName(selectedKeyForMenu.name);
              setOpenEditModal(true);
            }
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setKeyToDelete(selectedKeyForMenu);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          style={{ color: '#F04438' }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Modal */}
      <Dialog open={openCreateModal} onClose={() => setOpenCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create AI API Key</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="text"
                value={createData.name}
                onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                placeholder="e.g., Production AI Key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                AI SDK Provider <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <select
                value={createData.aiType}
                onChange={(e) => setCreateData({ ...createData, aiType: e.target.value as AiLlmTypes.AiType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontSize: '14px' }}
              >
                <option value={AiLlmTypes.AiType.OPENAI}>OpenAI SDK</option>
              </select>
              <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                API keys use the OpenAI-compatible SDK format
              </p>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setOpenCreateModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateApiKey}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#617BFF' }}
          >
            Create
          </button>
        </DialogActions>
      </Dialog>

      {/* SECRET DISPLAY MODAL - ONE TIME ONLY! */}
      <Dialog
        open={openSecretModal}
        onClose={() => {
          if (secretCopied) {
            setOpenSecretModal(false);
            setCreatedKey(null);
            setSecretCopied(false);
            setKeyCopied(false);
          }
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={!secretCopied}
      >
        <DialogTitle style={{ color: '#DC2626', fontWeight: 600 }}>
          ⚠️ Save Your API Secret Now!
        </DialogTitle>
        <DialogContent>
          <div
            className="p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: '#FEF3F2',
              borderColor: '#FECDCA',
            }}
          >
            <Typography style={{ fontSize: '14px', color: '#B42318', fontWeight: 600 }}>
              IMPORTANT: This secret will only be shown ONCE!
            </Typography>
            <Typography style={{ fontSize: '13px', color: '#B42318', marginTop: '4px' }}>
              Copy and save it securely now. You won't be able to retrieve it again.
            </Typography>
          </div>

          <div className="space-y-4">
            {/* API Key */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                API Key
              </label>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 px-3 py-2 rounded font-mono text-sm"
                  style={{
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    color: '#374151',
                  }}
                >
                  {createdKey?.key}
                </code>
                <button
                  onClick={() => createdKey && copyToClipboard(createdKey.key, 'key')}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  style={{ fontSize: '14px' }}
                >
                  {keyCopied ? <CheckCircleIcon fontSize="small" color="success" /> : 'Copy'}
                </button>
              </div>
            </div>

            {/* API Secret */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#DC2626' }}>
                API Secret (One-time display!)
              </label>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 px-3 py-2 rounded font-mono text-sm"
                  style={{
                    backgroundColor: '#FEF3F2',
                    border: '1px solid #FECDCA',
                    color: '#B42318',
                  }}
                >
                  {createdKey?.secret}
                </code>
                <button
                  onClick={() => createdKey && copyToClipboard(createdKey.secret, 'secret')}
                  className="px-3 py-2 rounded-lg text-white"
                  style={{ fontSize: '14px', backgroundColor: '#DC2626' }}
                >
                  {secretCopied ? <CheckCircleIcon fontSize="small" /> : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => {
              setOpenSecretModal(false);
              setCreatedKey(null);
              setSecretCopied(false);
              setKeyCopied(false);
            }}
            disabled={!secretCopied}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: secretCopied ? '#10B981' : '#9CA3AF' }}
          >
            {secretCopied ? "I've saved it ✓" : 'Copy secret first'}
          </button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
              Name
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ fontSize: '14px' }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setOpenEditModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleEditApiKey}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#617BFF' }}
          >
            Update
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the API key "{keyToDelete?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setDeleteDialogOpen(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteApiKey}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#F04438' }}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AIApiKeys;
